// Lightweight email-CSS lint. Scans author-written HTML for CSS that email clients
// (Outlook/Gmail especially) drop or mis-render, and returns human-readable warnings.
// Used by the HTML section/raw editor and surfaced alongside preflight.

export interface EmailLintIssue {
  level: 'warn' | 'info'
  message: string
}

export function lintEmailHtml(html: string): EmailLintIssue[] {
  if (!html) return []
  const issues: EmailLintIssue[] = []
  const add = (level: EmailLintIssue['level'], message: string) => issues.push({ level, message })
  const has = (re: RegExp) => re.test(html)

  if (has(/display\s*:\s*(inline-)?flex/i)) add('warn', 'display:flex is unsupported in Outlook — use tables for layout.')
  if (has(/display\s*:\s*grid/i)) add('warn', 'CSS grid is unsupported in most clients — use tables for layout.')
  if (has(/position\s*:\s*(absolute|fixed|sticky)/i)) add('warn', 'position:absolute/fixed/sticky is stripped by most clients.')
  if (has(/\b\d*\.?\d+(vh|vw|vmin|vmax)\b/i)) add('warn', 'Viewport units (vh/vw) are unreliable in email — use px or %.')
  if (has(/transform\s*:/i)) add('warn', 'CSS transforms are ignored by most email clients.')
  if (has(/(animation\s*:|@keyframes|transition\s*:)/i)) add('info', 'Animations/transitions are ignored by Outlook and most webmail.')
  if (has(/@import/i)) add('warn', '@import is blocked by most clients — use inline styles or a <style> block.')
  if (has(/<link[^>]+stylesheet/i)) add('warn', 'External stylesheets (<link>) are stripped — inline your CSS instead.')
  if (has(/object-fit\s*:/i)) add('info', 'object-fit is not supported in Outlook.')
  if (has(/[^-]gap\s*:/i)) add('info', 'flex/grid gap is unsupported — use cell padding or spacer rows.')
  if (has(/background(-image)?\s*:\s*[^;"']*url\(/i)) add('info', 'CSS background images do not render in Outlook — provide a bgcolor fallback.')
  if (has(/<style/i)) add('info', '<style> is inlined on export for client compatibility.')

  return issues
}
